import {
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

type HelpSection = {
  key: string;
  title: string;
  description: string;
  actions: string[];
  modulesTitle: string;
  modules: string[];
};

const HelpCenter = () => {
  const { t } = useTranslation();

  const handleScrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const sections: HelpSection[] = [
    {
      key: 'dashboard',
      title: t('helpCenter.sections.dashboard.title', { defaultValue: 'Dashboard principal' }),
      description: t('helpCenter.sections.dashboard.description', {
        defaultValue:
          'El dashboard principal concentra el estado operativo general de conciliaciones e importaciones. Está pensado para detectar desvíos, cuellos de botella y evolución del proceso sin tener que entrar a cada reporte.',
      }),
      modulesTitle: t('helpCenter.sections.dashboard.modulesTitle', {
        defaultValue: 'Módulos y datos que vas a ver',
      }),
      modules: [
        t('helpCenter.sections.dashboard.modules.one', {
          defaultValue:
            'Panel de actualización: muestra la última fecha/hora de refresh y el contador para próxima actualización.',
        }),
        t('helpCenter.sections.dashboard.modules.two', {
          defaultValue:
            'Conciliaciones por país: tabla resumen con OK/errores por país; al seleccionar un país se carga el detalle inferior.',
        }),
        t('helpCenter.sections.dashboard.modules.three', {
          defaultValue:
            'Importación fiscal e importación de maestros: tarjetas de estado para verificar procesamiento, pendientes y errores.',
        }),
        t('helpCenter.sections.dashboard.modules.four', {
          defaultValue:
            'Tarjetas de estado por período: métricas de conciliación para ayer, mes actual y mes anterior.',
        }),
        t('helpCenter.sections.dashboard.modules.five', {
          defaultValue:
            'Totales por período: comparación de montos y cantidad de registros entre fuentes, útil para control de consistencia.',
        }),
        t('helpCenter.sections.dashboard.modules.six', {
          defaultValue:
            'Evolución de cierres: gráfico temporal de tendencia OK/NOK y tabla de soporte para análisis histórico.',
        }),
      ],
      actions: [
        t('helpCenter.sections.dashboard.actions.one', {
          defaultValue: 'Revisar el último refresco y forzar actualización de datos.',
        }),
        t('helpCenter.sections.dashboard.actions.two', {
          defaultValue: 'Seleccionar país para desbloquear métricas y detalle específico.',
        }),
        t('helpCenter.sections.dashboard.actions.three', {
          defaultValue: 'Analizar tendencias y totales por período.',
        }),
      ],
    },
    {
      key: 'header',
      title: t('helpCenter.sections.conciliationHeader.title', {
        defaultValue: 'Reporte Encabezado',
      }),
      description: t('helpCenter.sections.conciliationHeader.description', {
        defaultValue:
          'Muestra la conciliación consolidada por encabezado de cierre. Es la vista recomendada para análisis global y para navegar luego al detalle de diferencias.',
      }),
      modulesTitle: t('helpCenter.sections.conciliationHeader.modulesTitle', {
        defaultValue: 'Módulos y datos que vas a ver',
      }),
      modules: [
        t('helpCenter.sections.conciliationHeader.modules.one', {
          defaultValue:
            'Bloque de filtros: permite acotar por país, local, fechas y estado de conciliación para reducir el universo consultado.',
        }),
        t('helpCenter.sections.conciliationHeader.modules.two', {
          defaultValue:
            'Tarjetas de información: resumen rápido de volumen de registros y estado global de la consulta.',
        }),
        t('helpCenter.sections.conciliationHeader.modules.three', {
          defaultValue:
            'Tabla principal: lista los encabezados conciliados con columnas operativas, orden, paginación y exportación.',
        }),
      ],
      actions: [
        t('helpCenter.sections.conciliationHeader.actions.one', {
          defaultValue: 'Aplicar filtros por país, fechas, estado y otros criterios.',
        }),
        t('helpCenter.sections.conciliationHeader.actions.two', {
          defaultValue: 'Consultar y ordenar resultados en la tabla.',
        }),
      ],
    },
    {
      key: 'detail',
      title: t('helpCenter.sections.conciliationDetail.title', {
        defaultValue: 'Reporte Detalle',
      }),
      description: t('helpCenter.sections.conciliationDetail.description', {
        defaultValue:
          'Permite profundizar en diferencias y errores a nivel transaccional. Se usa para explicar por qué un encabezado no concilia correctamente.',
      }),
      modulesTitle: t('helpCenter.sections.conciliationDetail.modulesTitle', {
        defaultValue: 'Módulos y datos que vas a ver',
      }),
      modules: [
        t('helpCenter.sections.conciliationDetail.modules.one', {
          defaultValue:
            'Bloque de filtros de detalle: refina por país, local, fechas y categorías de error para encontrar casos específicos.',
        }),
        t('helpCenter.sections.conciliationDetail.modules.two', {
          defaultValue:
            'Tarjetas de soporte: resumen de conteos por tipo de diferencia para priorizar investigación.',
        }),
        t('helpCenter.sections.conciliationDetail.modules.three', {
          defaultValue:
            'Tabla de detalle: muestra registros individuales con estados y diferencias para análisis de causa raíz.',
        }),
      ],
      actions: [
        t('helpCenter.sections.conciliationDetail.actions.one', {
          defaultValue: 'Filtrar por fechas, país, local y criterios de detalle.',
        }),
        t('helpCenter.sections.conciliationDetail.actions.two', {
          defaultValue: 'Identificar rápidamente tipos de error y desvíos.',
        }),
      ],
    },
    {
      key: 'synchro',
      title: t('helpCenter.sections.synchro.title', { defaultValue: 'Synchro' }),
      description: t('helpCenter.sections.synchro.description', {
        defaultValue:
          'Centraliza las ejecuciones de Synchro4Me para gestionar envíos y controlar su resultado operativo.',
      }),
      modulesTitle: t('helpCenter.sections.synchro.modulesTitle', {
        defaultValue: 'Módulos y datos que vas a ver',
      }),
      modules: [
        t('helpCenter.sections.synchro.modules.one', {
          defaultValue:
            'Filtros de ejecución: país, local y rango contable para consultar solo el historial relevante.',
        }),
        t('helpCenter.sections.synchro.modules.two', {
          defaultValue:
            'Acciones operativas: botón para crear ejecución y envío de solicitud de procesamiento.',
        }),
        t('helpCenter.sections.synchro.modules.three', {
          defaultValue:
            'Tabla de historial: estado, usuario, fecha de ejecución y mensajes de resultado por envío.',
        }),
      ],
      actions: [
        t('helpCenter.sections.synchro.actions.one', {
          defaultValue: 'Filtrar ejecuciones por país, local y rango contable.',
        }),
        t('helpCenter.sections.synchro.actions.two', {
          defaultValue: 'Crear nuevas ejecuciones y revisar estado/mensajes.',
        }),
      ],
    },
    {
      key: 'management',
      title: t('helpCenter.sections.management.title', {
        defaultValue: 'Reporte de Gestión',
      }),
      description: t('helpCenter.sections.management.description', {
        defaultValue:
          'Presenta indicadores operativos consolidados por fecha contable para monitorear eficiencia de cierre y calidad de conciliación.',
      }),
      modulesTitle: t('helpCenter.sections.management.modulesTitle', {
        defaultValue: 'Módulos y datos que vas a ver',
      }),
      modules: [
        t('helpCenter.sections.management.modules.one', {
          defaultValue:
            'Filtros de consulta: país y rango de fechas con validaciones de rango máximo permitido.',
        }),
        t('helpCenter.sections.management.modules.two', {
          defaultValue:
            'Tabla de indicadores: total de tiendas, tiendas OK, no cerradas, eficiencia y métricas de cupones.',
        }),
        t('helpCenter.sections.management.modules.three', {
          defaultValue:
            'Mensajes de validación: alertas cuando el rango excede límites operativos definidos.',
        }),
      ],
      actions: [
        t('helpCenter.sections.management.actions.one', {
          defaultValue: 'Seleccionar país y rango de fechas para la consulta.',
        }),
        t('helpCenter.sections.management.actions.two', {
          defaultValue: 'Analizar tiendas OK, no cerradas y cupones con error.',
        }),
      ],
    },
    {
      key: 'stores',
      title: t('helpCenter.sections.parametricStores.title', {
        defaultValue: 'Parametría de Locales',
      }),
      description: t('helpCenter.sections.parametricStores.description', {
        defaultValue:
          'Permite administrar el catálogo de locales paramétricos utilizado por los procesos operativos y de conciliación.',
      }),
      modulesTitle: t('helpCenter.sections.parametricStores.modulesTitle', {
        defaultValue: 'Módulos y datos que vas a ver',
      }),
      modules: [
        t('helpCenter.sections.parametricStores.modules.one', {
          defaultValue:
            'Tabla de parametría: país, código, local y nombre del local para control maestro de registros.',
        }),
        t('helpCenter.sections.parametricStores.modules.two', {
          defaultValue:
            'Alta de registro: formulario para crear nuevos locales paramétricos con validaciones.',
        }),
        t('helpCenter.sections.parametricStores.modules.three', {
          defaultValue:
            'Edición y eliminación: acciones para mantener datos consistentes y actualizados.',
        }),
      ],
      actions: [
        t('helpCenter.sections.parametricStores.actions.one', {
          defaultValue: 'Crear nuevos registros paramétricos por país y código.',
        }),
        t('helpCenter.sections.parametricStores.actions.two', {
          defaultValue: 'Editar y eliminar registros existentes según necesidad.',
        }),
      ],
    },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        py: 3,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={4} lg={3} sx={{ display: 'flex' }}>
          <Card
            sx={{
              position: { md: 'sticky' },
              top: { md: 88 },
              width: '100%',
              maxHeight: { md: 'calc(100vh - 104px)' },
              overflowY: { md: 'auto' },
            }}
          >
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="h6" fontWeight={700}>
                  {t('helpCenter.menuTitle', { defaultValue: 'Navegación' })}
                </Typography>
                <Divider />
                <List dense disablePadding>
                  {sections.map(section => (
                    <ListItem key={`menu-${section.key}`} disablePadding>
                      <ListItemButton
                        onClick={() => handleScrollToSection(`help-section-${section.key}`)}
                      >
                        <ListItemText primary={section.title} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <Stack spacing={2}>
            <Typography variant="h4" fontWeight={700}>
              {t('helpCenter.title', { defaultValue: 'Ayuda e instructivo' })}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('helpCenter.subtitle', {
                defaultValue:
                  'En esta pantalla encontrarás una guía funcional del dashboard y de cada reporte principal para entender qué información se muestra y qué acciones puedes realizar.',
              })}
            </Typography>

            {sections.map(section => (
              <Card
                key={section.key}
                id={`help-section-${section.key}`}
                sx={{ scrollMarginTop: { xs: 72, md: 88 } }}
              >
                <CardContent>
                  <Stack spacing={1.5}>
                    <Typography variant="h6" fontWeight={700}>
                      {section.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {section.description}
                    </Typography>
                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>
                      {section.modulesTitle}
                    </Typography>
                    <List dense disablePadding>
                      {section.modules.map(module => (
                        <ListItem key={module} disableGutters sx={{ py: 0.25 }}>
                          <ListItemText primary={module} />
                        </ListItem>
                      ))}
                    </List>
                    <Divider />
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t('helpCenter.canDo', { defaultValue: 'Qué puedes hacer aquí' })}
                    </Typography>
                    <List dense disablePadding>
                      {section.actions.map(action => (
                        <ListItem key={action} disableGutters sx={{ py: 0.25 }}>
                          <ListItemText primary={action} />
                        </ListItem>
                      ))}
                    </List>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default HelpCenter;
